import React from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, DollarSign, TrendingUp, MinusCircle } from 'lucide-react';
import { format } from 'date-fns';

const CasesModal = ({ cases, title, onClose, isAdmin, financialSummary }) => {
  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      under_review: 'bg-purple-100 text-purple-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-slate-100 text-slate-800',
      new: 'bg-cyan-100 text-cyan-800'
    };
    return statusClasses[status] || statusClasses.pending;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-600 mt-1">{cases.length} case{cases.length !== 1 ? 's' : ''} found</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Financial Summary for Resolved Cases */}
        {financialSummary && (
          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="text-green-600" size={20} />
              Financial Summary - Resolved Cases
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="text-green-600" size={18} />
                  <span className="text-xs text-slate-600 font-medium">Total Recovered</span>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  ${financialSummary.total_recovered.toLocaleString()}
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <MinusCircle className="text-purple-600" size={18} />
                  <span className="text-xs text-slate-600 font-medium">Referral Commissions</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  ${financialSummary.total_commissions_paid.toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {financialSummary.commission_rate.toFixed(1)}% of total
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="text-blue-600" size={18} />
                  <span className="text-xs text-slate-600 font-medium">Net Settled Amount</span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  ${financialSummary.net_settled.toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  After commissions
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 text-white shadow-sm">
                <div className="text-xs font-medium mb-2 opacity-90">Average Recovery</div>
                <div className="text-2xl font-bold">
                  ${(financialSummary.total_recovered / cases.length).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </div>
                <div className="text-xs opacity-75 mt-1">
                  per case
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cases List */}
        <div className="flex-1 overflow-y-auto p-6">
          {cases.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No cases found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cases.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={isAdmin ? `/admin/case/${caseItem.id}` : `/case/${caseItem.id}`}
                  className="block bg-white border border-slate-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900 group-hover:text-purple-600 transition-colors">
                          {caseItem.company_name}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(caseItem.status)}`}>
                          {caseItem.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {caseItem.is_referral && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                            REFERRAL
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Client:</span>
                          <p className="font-medium text-slate-700">{caseItem.client_name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Frozen Amount:</span>
                          <p className="font-medium text-slate-900">${caseItem.frozen_amount?.toLocaleString() || 0}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Processor:</span>
                          <p className="font-medium text-slate-700">{caseItem.processor_name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Freeze Date:</span>
                          <p className="font-medium text-slate-700">
                            {caseItem.freeze_date ? format(new Date(caseItem.freeze_date), 'MMM dd, yyyy') : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="text-purple-600" size={20} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CasesModal;
